import React, { useCallback, useEffect, useMemo, useState } from "react";
import BreadCrumb from "Common/BreadCrumb";
import { Plus, Trash2, ArrowLeft } from "lucide-react";
import TableContainer from "Common/TableContainer";
import DeleteModal from "Common/DeleteModal";
import Modal from "Common/Components/Modal";
import Select from "react-select";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import LoadingCounter from "Common/Components/LoadingCounterProps";
import selectStyles from "Common/constants/selectStyles";
import useSearchableOptions from "Common/hooks/useSearchableOptions";
import { apiErrorMessage } from "Common/constants/apiErrors";
import { ROLE } from "Common/constants/roles";

const BASE_URL = "https://portal-api.mahanpanel.com";

// SalesSubstitutionUser
interface SubstitutionUser {
  id: number;
  username: string;
  fullname?: string | null;
  role_id: number;
  disabled: boolean;
}

// SalesSubstitutionOut
interface Substitution {
  id: number;
  current_user_id: number;
  substitute_user_id: number;
  current_user: SubstitutionUser;
  substitute_user: SubstitutionUser;
  created_at_jalali?: string | null;
}

const authHeaders = () => {
  const token =
    localStorage.getItem("app_at_mahan") || sessionStorage.getItem("app_at_mahan");
  return { Authorization: `Bearer ${token}` };
};

/** نام نمایشی کاربر: اول نام کامل، در نبودش نام کاربری. */
const displayName = (user?: SubstitutionUser | null): string =>
  (user?.fullname || "").trim() || user?.username || "—";

const Index = () => {
  const navigate = useNavigate();

  const [rows, setRows] = useState<Substitution[]>([]);
  const [users, setUsers] = useState<SubstitutionUser[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [reload, setReload] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [sortBy, setSortBy] = useState<string | undefined>();
  const [sortOrder, setSortOrder] = useState<"default" | "asc" | "desc">("default");
  const changeServerSort = useCallback((nextSortBy: string | undefined, nextSortOrder: "default" | "asc" | "desc") => {
    setSortBy(nextSortBy);
    setSortOrder(nextSortOrder);
  }, []);

  const [show, setShow] = useState<boolean>(false);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [currentUserId, setCurrentUserId] = useState<string>("");
  const [substituteUserId, setSubstituteUserId] = useState<string>("");

  const [deleteModal, setDeleteModal] = useState<boolean>(false);
  const [eventData, setEventData] = useState<Substitution | null>(null);

  // ========== فهرست جایگزینی‌ها ==========
  useEffect(() => {
    const fetchRows = async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${BASE_URL}/sales-substitutions/`, {
          headers: authHeaders(),
          params: sortOrder === "default" ? { sort_order: "default" } : { sort_by: sortBy, sort_order: sortOrder },
        });
        const items: Substitution[] = Array.isArray(res.data)
          ? res.data
          : res.data?.items || [];
        setRows(items);
        setErrorMessage("");
      } catch (error: any) {
        console.error("Error fetching substitutions:", error);
        if (error.response?.status === 401) {
          navigate("/login");
          return;
        }
        setErrorMessage(
          apiErrorMessage(error, "مشکلی در دریافت فهرست جایگزینی‌ها رخ داده است.")
        );
      } finally {
        setLoading(false);
      }
    };
    fetchRows();
  }, [reload, navigate, sortBy, sortOrder]);

  // ========== کاربران قابل انتخاب ==========
  // مدیر فروش برای این انتخابگر از endpoint اختصاصی استفاده می‌کند؛ /users فقط ادمین است.
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const res = await axios.get(`${BASE_URL}/sales-substitutions/sales-experts`, {
          headers: authHeaders(),
        });
        const collected: SubstitutionUser[] = Array.isArray(res.data) ? res.data : res.data?.items || [];
        setUsers(collected.filter((user) => user?.role_id === ROLE.SALES_EXPERT && !user?.disabled));
      } catch (error: any) {
        console.error("Error fetching users:", error);
        if (error.response?.status === 401) navigate("/login");
      }
    };
    fetchUsers();
  }, [navigate]);

  const userSearch = useSearchableOptions(
    users,
    (user) => displayName(user),
    (user) => String(user.id)
  );

  const toggle = useCallback(() => {
    setShow((prev) => !prev);
    setCurrentUserId("");
    setSubstituteUserId("");
  }, []);

  const optionFor = (id: string) => {
    if (!id) return null;
    const user = users.find((u) => String(u.id) === id);
    return user ? { value: String(user.id), label: displayName(user) } : null;
  };

  // ========== افزودن ==========
  const handleSubmit = async () => {
    if (!currentUserId || !substituteUserId) {
      Swal.fire("خطا", "هر دو فیلد «کاربر فعلی» و «کاربر جایگزین» الزامی هستند.", "error");
      return;
    }
    if (currentUserId === substituteUserId) {
      Swal.fire("خطا", "کاربر فعلی و کاربر جایگزین نمی‌توانند یکی باشند.", "error");
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(
        `${BASE_URL}/sales-substitutions/`,
        {
          current_user_id: Number(currentUserId),
          substitute_user_id: Number(substituteUserId),
        },
        { headers: { ...authHeaders(), "Content-Type": "application/json" } }
      );
      if (res.status >= 200 && res.status < 300) {
        Swal.fire("عملیات موفق", "جایگزینی با موفقیت ثبت شد", "success");
        setReload((prev) => !prev);
        toggle();
      }
    } catch (error: any) {
      console.error("Substitution create error:", error);
      if (error.response?.status === 401) {
        navigate("/login");
        return;
      }
      // ۴۰۹ یعنی همین جفت قبلاً ثبت شده است؛ ۴۲۲ یعنی انتخاب نامعتبر.
      Swal.fire("خطا", apiErrorMessage(error, "مشکل در ثبت جایگزینی"), "error");
    } finally {
      setSubmitting(false);
    }
  };

  // ========== حذف ==========
  const handleDelete = async () => {
    if (!eventData) return;
    try {
      const res = await axios.delete(`${BASE_URL}/sales-substitutions/${eventData.id}`, {
        headers: authHeaders(),
      });
      if (res.status >= 200 && res.status < 300) {
        Swal.fire(
          "عملیات موفق",
          "جایگزینی حذف شد و دسترسی کاربر جایگزین به حالت عادی برگشت.",
          "success"
        );
        // فهرست دوباره از سرور خوانده می‌شود تا وضعیت نمایش‌داده‌شده با سرور یکی بماند.
        setReload((prev) => !prev);
      }
    } catch (error: any) {
      console.error("Substitution delete error:", error);
      if (error.response?.status === 401) {
        navigate("/login");
        return;
      }
      Swal.fire("خطا", apiErrorMessage(error, "مشکل در حذف جایگزینی"), "error");
    } finally {
      setDeleteModal(false);
      setEventData(null);
    }
  };

  const columns = useMemo(
    () => [
      {
        header: "کاربر فعلی",
        accessorKey: "current_user_id",
        meta: { serverSortBy: "current_user_name" },
        cell: (cell: any) => {
          const row: Substitution = cell.row.original;
          return (
            <div className="flex flex-col">
              <span className="font-medium">{displayName(row.current_user)}</span>
              <span className="text-xs text-blue-800 dark:text-zink-300">
                {row.current_user?.username}
              </span>
            </div>
          );
        },
      },
      {
        header: "",
        id: "arrow",
        enableColumnFilter: false,
        exportable: false,
        cell: () => <ArrowLeft className="size-4 text-slate-400" />,
      },
      {
        header: "کاربر جایگزین",
        accessorKey: "substitute_user_id",
        meta: { serverSortBy: "substitute_user_name" },
        cell: (cell: any) => {
          const row: Substitution = cell.row.original;
          return (
            <div className="flex flex-col">
              <span className="font-medium">{displayName(row.substitute_user)}</span>
              <span className="text-xs text-blue-800 dark:text-zink-300">
                {row.substitute_user?.username}
              </span>
            </div>
          );
        },
      },
      {
        header: "تاریخ ثبت",
        accessorKey: "created_at_jalali",
        cell: (cell: any) => cell.getValue() || "—",
      },
      {
        header: "عملیات",
        enableColumnFilter: false,
        exportable: false,
        cell: (cell: any) => (
          <button
            type="button"
            onClick={() => {
              setEventData(cell.row.original);
              setDeleteModal(true);
            }}
            className="text-red-500 hover:text-red-700"
            title="حذف جایگزینی"
          >
            <Trash2 className="size-4" />
          </button>
        ),
      },
    ],
    []
  );

  return (
    <React.Fragment>
      <BreadCrumb title="تعیین جایگزین" pageTitle="داده‌های اولیه" />
      <DeleteModal
        show={deleteModal}
        onHide={() => setDeleteModal(false)}
        onDelete={handleDelete}
      />

      {errorMessage && <div className="mb-3 text-center text-red-500">{errorMessage}</div>}

      <div className="card">
        <div className="card-body">
          {/* توضیح کوتاه کاربرد، چون این صفحه رفتار بقیه‌ی بخش‌ها را عوض می‌کند. */}
          <div className="p-3 mb-4 text-xs leading-6 border rounded-lg bg-sky-50 border-sky-100 text-sky-900 dark:bg-zink-700 dark:border-zink-600 dark:text-zink-100">
            کاربر جایگزین، علاوه بر سفارش‌های خودش، سفارش‌های «کاربر فعلی» را هم می‌بیند و
            عملیات مجاز کارشناس فروش را روی آن‌ها انجام می‌دهد. نام کارشناس فروشِ سفارش
            تغییر نمی‌کند و سفارش جدیدی که جایگزین ثبت کند متعلق به خودش است. با حذف این
            رکورد، دسترسی بدون نیاز به خروج و ورود مجدد به حالت عادی برمی‌گردد.
          </div>

          <div className="flex items-center gap-3 mb-4">
            <h6 className="text-15 grow">
              مجموع جایگزینی‌ها (<b className="total-Employs">{rows.length}</b>)
            </h6>
            <div className="shrink-0">
              <button
                type="button"
                onClick={() => setShow(true)}
                className="text-white btn bg-custom-500 border-custom-500 hover:bg-custom-600"
              >
                <span className="align-middle">تعیین جایگزین</span>{" "}
                <Plus className="inline-block size-4" />
              </button>
            </div>
          </div>

          {loading ? (
            <div className="flex items-center justify-center h-32">
              <div className="inline-block w-8 h-8 border-4 rounded-full animate-spin border-custom-500 border-t-transparent"></div>
            </div>
          ) : rows.length > 0 ? (
            <TableContainer
              isPagination={true}
              columns={columns}
              data={rows}
              serverSorting={{ sortBy, sortOrder, onChange: changeServerSort }}
              customPageSize={10}
              divclassName="-mx-5 overflow-x-auto"
              tableclassName="w-full whitespace-nowrap"
              theadclassName="ltr:text-left rtl:text-right bg-[#e2e8f0] text-[#334155] dark:bg-[#334155] dark:text-[#f1f5f9]"
              thclassName="px-3.5 py-2.5 first:pl-5 last:pr-5 font-semibold border-b border-slate-300 dark:border-slate-600"
              tdclassName="px-3.5 py-2.5 first:pl-5 last:pr-5 border-y border-slate-200 dark:border-zink-400/80"
              PaginationClassName="flex flex-col items-center gap-4 px-4 mt-4 md:flex-row"
            />
          ) : (
            <LoadingCounter count={1} text="هیچ جایگزینی ثبت نشده است" />
          )}
        </div>
      </div>

      {/* ========== مودال تعیین جایگزین ========== */}
      <Modal show={show} onHide={toggle} modal-center="true" dialogClassName="w-screen md:w-[36rem]">
        <Modal.Header
          className="flex items-center justify-between p-4 border-b dark:border-zink-500"
          closeButtonClass="transition-all duration-200 ease-linear text-slate-500 hover:text-red-500 dark:text-zink-200 dark:hover:text-red-500"
        >
          <Modal.Title className="text-16">تعیین جایگزین</Modal.Title>
        </Modal.Header>
        <Modal.Body className="max-h-[calc(100vh-220px)] p-4 overflow-y-auto">
          <div className="space-y-4" dir="rtl">
            <div>
              <label className="block mb-1 text-base font-medium">
                کاربر فعلی: <span className="text-red-500">*</span>
              </label>
              <Select
                options={userSearch.options}
                onInputChange={userSearch.onInputChange}
                value={optionFor(currentUserId)}
                onChange={(opt: any) => setCurrentUserId(opt?.value || "")}
                isClearable
                placeholder="انتخاب کارشناس فروش..."
                noOptionsMessage={() => "کارشناس فروش فعالی یافت نشد"}
                styles={selectStyles}
                menuPortalTarget={document.body}
                menuPosition="fixed"
                classNamePrefix="react-select"
              />
            </div>

            <div>
              <label className="block mb-1 text-base font-medium">
                کاربر جایگزین: <span className="text-red-500">*</span>
              </label>
              <Select
                options={userSearch.options.filter(
                  (opt: any) => opt.value !== currentUserId
                )}
                onInputChange={userSearch.onInputChange}
                value={optionFor(substituteUserId)}
                onChange={(opt: any) => setSubstituteUserId(opt?.value || "")}
                isClearable
                placeholder="انتخاب کارشناس فروش..."
                noOptionsMessage={() => "کارشناس فروش فعالی یافت نشد"}
                styles={selectStyles}
                menuPortalTarget={document.body}
                menuPosition="fixed"
                classNamePrefix="react-select"
              />
            </div>

            {currentUserId && substituteUserId && (
              <p className="text-xs text-slate-500 dark:text-zink-300">
                {displayName(users.find((u) => String(u.id) === substituteUserId))} سفارش‌های{" "}
                {displayName(users.find((u) => String(u.id) === currentUserId))} را هم خواهد دید.
                این دسترسی یک‌طرفه است.
              </p>
            )}
          </div>

          <div className="flex justify-end gap-2 mt-5">
            <button
              type="button"
              onClick={toggle}
              className="bg-white btn border-slate-300 text-slate-600 hover:bg-slate-100 dark:bg-zink-700 dark:border-zink-500 dark:text-zink-100"
            >
              انصراف
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={submitting || !currentUserId || !substituteUserId}
              className="text-white btn bg-custom-500 border-custom-500 hover:bg-custom-600 disabled:opacity-60"
            >
              {submitting ? "در حال ثبت..." : "ثبت"}
            </button>
          </div>
        </Modal.Body>
      </Modal>
    </React.Fragment>
  );
};

export default Index;
